import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, FileText } from "lucide-react";
import { UrlContext } from "../../contexts/UrlContext";

export default function ExistingDigital({ exp_id, proj_id }) {
  const [showExistingDigital, setShowExistingDigital] = useState(false);
  const [existingDigitalData, setExistingDigitalData] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);
  const { API_URL } = useContext(UrlContext);

  useEffect(() => {
    const url = `${API_URL}get-existing-digital-ppt`;

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ exp_id }),
    })
      .then((res) => res.json())
      .then((data) => {
        setExistingDigitalData(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.log(err);
        setExistingDigitalData([]);
      });
  }, [API_URL, exp_id]);

  const closePopup = () => {
    setHoverIndex(null);
    setShowExistingDigital(false);
  };

  const popup =
    showExistingDigital && typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence>
            <motion.div
              className="fixed inset-0 z-[10060] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm sm:p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              role="presentation"
              data-pdp2-print-hide="true"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closePopup();
                }
              }}
            >
              <motion.div
                className="pdp2-section-picker flex max-h-[90vh] w-[min(1080px,calc(100vw-32px))] flex-col overflow-hidden rounded-[26px] border border-slate-200/90 bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_34%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_28px_90px_rgba(15,23,42,0.34)]"
                initial={{ opacity: 0, y: 22, scale: 0.975 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{
                  type: "spring",
                  stiffness: 340,
                  damping: 30,
                  mass: 0.82,
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pdp2-existing-digital-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="pdp2-section-picker-header">
                  <div className="pdp2-section-picker-title-wrap">
                    <span className="pdp2-section-picker-eyebrow">
                      Digital profile history
                    </span>
                    <strong id="pdp2-existing-digital-title">
                      Existing digital profiles
                    </strong>
                    <p>
                      Open a previously created digital profile for this
                      consultant in a new tab.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="pdp2-section-picker-close"
                    onClick={closePopup}
                    aria-label="Close existing digital profiles"
                  >
                    ×
                  </button>
                </div>

                <div className="pdp2-section-picker-body">
                  <div className="p-4 sm:p-5">
                    {existingDigitalData.length > 0 ? (
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                        {existingDigitalData.map((item, index) => {
                          const isHovered = hoverIndex === index;

                          return (
                            <a
                              key={
                                item.ID ||
                                item.id ||
                                `${item.projectID}-${item.expertID}-${index}`
                              }
                              href={`/app/ExistingDigitalPPT/${item.projectID}/${item.expertID}/${"null"}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={closePopup}
                              onMouseEnter={() => setHoverIndex(index)}
                              onMouseLeave={() => setHoverIndex(null)}
                              className={[
                                "group relative flex min-h-[104px] items-start gap-3 rounded-2xl border p-4 text-left !no-underline hover:!no-underline focus:!no-underline transition duration-200",
                                isHovered
                                  ? "border-[#365561]/30 bg-[#365561]/10 shadow-[0_10px_26px_rgba(54,85,97,0.10)]"
                                  : "border-slate-200/90 bg-white/85 hover:border-[#365561]/25 hover:bg-white",
                              ].join(" ")}
                            >
                              <span
                                className={[
                                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition",
                                  isHovered
                                    ? "bg-[#365561] text-white"
                                    : "bg-[#365561]/10 text-[#365561]",
                                ].join(" ")}
                                aria-hidden="true"
                              >
                                <FileText size={19} strokeWidth={2} />
                              </span>

                              <span className="min-w-0 flex-1">
                                <span className="flex items-start justify-between gap-3">
                                  <strong className="block min-w-0 break-words text-[13px] font-semibold leading-[1.35] text-slate-800">
                                    {item.pptName || "Digital profile"}
                                  </strong>

                                  <ExternalLink
                                    size={15}
                                    strokeWidth={1.9}
                                    className={[
                                      "mt-0.5 shrink-0 transition",
                                      isHovered
                                        ? "text-[#365561]"
                                        : "text-slate-400 group-hover:text-[#365561]",
                                    ].join(" ")}
                                    aria-hidden="true"
                                  />
                                </span>

                                {item.summ ? (
                                  <span
                                    className={[
                                      "mt-2 block overflow-hidden text-[11.5px] leading-[1.45] text-slate-500 transition-all duration-200",
                                      isHovered
                                        ? "max-h-[92px]"
                                        : "max-h-[50px]",
                                    ].join(" ")}
                                    dangerouslySetInnerHTML={{
                                      __html: item.summ,
                                    }}
                                  />
                                ) : (
                                  <span className="mt-2 block text-[11.5px] leading-[1.45] text-slate-400">
                                    No summary available.
                                  </span>
                                )}

                                <span className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-medium text-[#365561] opacity-70 transition group-hover:opacity-100">
                                  Open existing profile
                                  <span aria-hidden="true">→</span>
                                </span>
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white/65 px-6 text-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#365561]/10 text-[#365561]">
                          <FileText size={22} strokeWidth={1.8} />
                        </span>

                        <strong className="mt-3 text-[14px] font-semibold text-slate-700">
                          No existing digital profile found
                        </strong>

                        <span className="mt-1 max-w-[420px] text-[12px] leading-[1.5] text-slate-500">
                          There are currently no previously generated digital
                          profiles available for this consultant.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <>
      <button
        type="button"
        className="pdp2-toolbar-button"
        onClick={() => setShowExistingDigital(true)}
      >
        Existing digital
      </button>

      {popup}
    </>
  );
}